import React, { useState } from "react";
import * as R from "ramda";
import { Switch, Tooltip, Card, Tag } from "antd";
import { connect } from "react-redux";
import { compose } from "recompose";
import { FormItem } from "@worcket/common/components/controls";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";

import Button from "../../../../../components/Button";
import { isLoading } from "../../../../../utils/actions";
import {
  publishOpportunityFeed,
  PUBLISH_OPPORTUNITY_FEED
} from "../../../../../store/actions/opportunity";
import { getCountry } from "../../../../../utils/data/static/countries";

import styled from "styled-components/macro";

const FormItemWrapper = styled.div`
  &:not(:last-of-type) {
    margin-right: 10px;
  }
  &:hover {
    box-shadow: 0 1px 8px 0 rgba(0, 0, 0, 0.2);
  }
  transition: 0.3s ease;

  .ant-card-cover {
    height: 110px;
    display: flex;
    justify-content: center;
    position: relative;
  }
  img {
    width: 75%;
  }
  .ant-card-body {
    display: none;
  }

  .ant-card-actions {
    height: 70px;
    display: flex;
    align-items: center;

    /* Reset ant-card-actions of loading button */
    & > li > span i {
      width: auto !important;
      color: currentColor !important;
    }
  }

  .ant-form-item {
    margin: 0;
  }
`;

const ProviderCountry = styled.div`
  display: flex !important;
  justify-content: center;
  flex-wrap: wrap;
  align-items: center;
  position: absolute;
  bottom: -12px;
  background-color: #fafafa;
  border-radius: 50%;
  display: flex;
  align-items: stretch;
  justify-content: center;
  width: 24px !important;
  height: 24px;
  overflow: hidden;
  img {
    width: 40px;
    height: initial;
  }
`;

const TooltipContainer = styled.article`
  text-align: center;
`;

const Status = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  position: absolute;
  top: 10px;
  align-items: center;
`;

const SocialItem = ({
  feed,
  logo,
  country,
  flag,
  feedConnected,
  form,
  name,
  opportunity,
  newOpportunity,
  datePublished,
  publishOpportunityFeed,
  loading,
  disabling,
  onConnect,
  connecting,
  errorPublished,
  loadingPages
}) => {
  const [diff, setDiff] = useState(
    moment(Date.now()).diff(datePublished, "minutes")
  );
  const { t } = useTranslation(["opportunity"]);
  const isNotPublished = opportunity.status !== "published";

  return (
    <FormItemWrapper>
      <Card
        style={{ maxWidth: 169 }}
        cover={
          <div style={{ display: "flex", justifyContent: "center" }}>
            <img
              alt={name}
              src={logo}
              style={{ marginTop: feed === "linkedin" ? 5 : 0 }}
            />
            {flag && (
              <Tooltip
                placement="top"
                title={
                  <TooltipContainer>
                    <div className="name-wrapper">{getCountry(country)}</div>
                  </TooltipContainer>
                }
              >
                <ProviderCountry>
                  <img src={flag} alt={country} />
                </ProviderCountry>
              </Tooltip>
            )}
            {feedConnected &&
              !newOpportunity &&
              (datePublished || errorPublished) && (
                <Status>
                  <Tooltip
                    title={
                      errorPublished
                        ? t("opportunity:wizard.multiposting.errorPublished", {
                            date: moment(errorPublished).format(
                              "DD/MM/YYYY HH:mm"
                            )
                          })
                        : t("opportunity:wizard.multiposting.publishedAt", {
                            date: moment(datePublished).format(
                              "DD/MM/YYYY HH:mm"
                            )
                          })
                    }
                  >
                    <Tag
                      color={errorPublished ? "red" : "green"}
                      data-test={`${feed}-account`}
                    >
                      {errorPublished
                        ? t("opportunity:wizard.multiposting.notPublished")
                        : t("opportunity:wizard.multiposting.published")}
                    </Tag>
                  </Tooltip>
                </Status>
              )}
          </div>
        }
        actions={[
          feedConnected ? (
            <>
              {newOpportunity ? (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  {t("opportunity:wizard.multiposting.publish")}
                  <FormItem
                    form={form}
                    name={name}
                    decoratorProps={{ valuePropName: "checked" }}
                    render={<Switch style={{ marginLeft: 10 }} />}
                  />
                </div>
              ) : datePublished || errorPublished ? (
                <Tooltip
                  title={
                    isNotPublished
                      ? t("opportunity:wizard.multiposting.isNotPublished")
                      : loading
                      ? t("opportunity:wizard.multiposting.publishing")
                      : diff < 10
                      ? t("opportunity:wizard.multiposting.waiting", {
                          diff: 10 - diff
                        })
                      : t("opportunity:wizard.multiposting.republish")
                  }
                  onVisibleChange={() =>
                    setDiff(moment(Date.now()).diff(datePublished, "minutes"))
                  }
                >
                  <Button
                    data-test={`${feed}-button-published`}
                    type="primary"
                    style={{ marginLeft: 10 }}
                    size="sm"
                    disabled={disabling || diff < 10 || isNotPublished}
                    onClick={async () => await publishOpportunityFeed(feed)}
                  >
                    {loading
                      ? t("opportunity:wizard.multiposting.publishing")
                      : t("opportunity:wizard.multiposting.republish")}
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip
                  title={
                    isNotPublished &&
                    t("opportunity:wizard.multiposting.isNotPublished")
                  }
                >
                  <Button
                    data-test={`${feed}-publish-button`}
                    type="primary"
                    size="sm"
                    style={{ marginLeft: 10 }}
                    disabled={disabling || isNotPublished}
                    onClick={async () => await publishOpportunityFeed(feed)}
                  >
                    {loading
                      ? t("opportunity:wizard.multiposting.publishing")
                      : t("opportunity:wizard.multiposting.publish")}
                  </Button>
                </Tooltip>
              )}
            </>
          ) : (
            <Button
              size="sm"
              loading={connecting || loadingPages}
              onClick={onConnect}
            >
              {connecting
                ? t("opportunity:wizard.multiposting.connecting")
                : t("opportunity:wizard.multiposting.connect")}
            </Button>
          )
        ]}
      ></Card>
    </FormItemWrapper>
  );
};

const enhancer = compose(
  connect(
    (state, { feed }) => ({
      opportunity: state.opportunity.selected,
      loading:
        (state.opportunity.loadingSite === feed &&
          isLoading(PUBLISH_OPPORTUNITY_FEED, state)) ||
        R.path(
          [
            "opportunity",
            "selected",
            "multiposting",
            "socialFeed",
            feed,
            "pending"
          ],
          state
        ),
      disabling:
        isLoading(PUBLISH_OPPORTUNITY_FEED, state) ||
        R.path(
          [
            "opportunity",
            "selected",
            "multiposting",
            "socialFeed",
            feed,
            "pending"
          ],
          state
        ),
      connecting: R.pathEq(
        ["company", "socialAccounts", "loading"],
        feed,
        state
      )
    }),
    {
      publishOpportunityFeed
    }
  )
);

export default enhancer(SocialItem);
